import { useState, useEffect, useRef } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, ArrowUpRight, ChevronDown } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

const NAV = [
  { to: "/", label: "Accueil" },
  {
    label: "Nos chapes",
    intro: "Traditionnelle, allégée, béton dur ou rampe d'accès.",
    items: [
      { to: "/chapes/chape-traditionnelle", label: "Chape traditionnelle", desc: "Mortier ciment et sable taloché." },
      { to: "/chapes/chape-allegee", label: "Chape allégée", desc: "Légère, idéale en rénovation." },
      { to: "/chapes/beton-dur", label: "Béton dur", desc: "Sols industriels haute résistance." },
      { to: "/chapes/rampe-acces", label: "Rampe d'accès garage", desc: "Béton rainuré antidérapant." },
    ],
  },
  {
    label: "Nos isolations",
    intro: "Confort thermique et acoustique sous chape.",
    items: [
      { to: "/isolations/isolation-thermique", label: "Isolation thermique", desc: "Moins de pertes, plus de confort." },
      { to: "/isolations/isolation-phonique", label: "Isolation phonique", desc: "Réduction des bruits d'impact." },
    ],
  },
  {
    label: "Nos solutions",
    intro: "Des réponses pour vos contraintes de chantier.",
    items: [
      { to: "/solutions/renovation-sols", label: "Rénovation des sols", desc: "Reprise et mise à niveau de l'existant." },
      { to: "/solutions/sechage-rapide", label: "Séchage rapide", desc: "Pour livrer plus vite." },
      { to: "/solutions/sols-trafic", label: "Sols à trafic intense", desc: "Ateliers, garages, entrepôts." },
    ],
  },
  { to: "/realisations", label: "Réalisations" },
  { to: "/a-propos", label: "À propos" },
  { to: "/contact", label: "Contact" },
];

export default function Header() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [mobileSub, setMobileSub] = useState(null);
  const [openMenu, setOpenMenu] = useState(null);
  const { pathname } = useLocation();
  const closeTimer = useRef(null);

  useEffect(() => {
    setMobileOpen(false);
    setOpenMenu(null);
    setMobileSub(null);
  }, [pathname]);

  const handleEnter = (label) => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
    setOpenMenu(label);
  };
  const handleLeave = () => {
    closeTimer.current = setTimeout(() => setOpenMenu(null), 180);
  };

  const slug = (s) => s.toLowerCase().replace(/\s+/g, "-").replace("à", "a");

  return (
    <header
      data-testid="site-header"
      className="absolute top-0 left-0 right-0 z-50 bg-[#F4F4F0]"
    >
      {/* ROW 1 — LOGO (slightly left of center) */}
      <div className="relative px-4 md:px-8 lg:px-14 pt-4 pb-2 xl:pt-5 xl:pb-3">
        <button
          data-testid="mobile-menu-toggle"
          className="xl:hidden absolute right-4 top-1/2 -translate-y-1/2 p-2 text-[#1A2D7E]"
          onClick={() => setMobileOpen((v) => !v)}
          aria-label="Menu"
        >
          {mobileOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>

        <Link
          to="/devis"
          data-testid="header-devis-cta"
          className="hidden xl:inline-flex items-center gap-2 absolute right-4 md:right-8 lg:right-14 top-1/2 -translate-y-1/2 bg-[#F08722] text-white px-5 py-3 text-xs uppercase tracking-[0.16em] font-semibold hover:bg-[#1A2D7E] transition-colors"
        >
          Demander un devis <ArrowUpRight className="w-4 h-4" />
        </Link>

        <div className="flex justify-center pl-10 pr-10 xl:pl-0 xl:pr-0 xl:-translate-x-[190px]">
          <Link to="/" data-testid="logo-link" className="flex items-center gap-3 sm:gap-5 group">
            <div className="relative w-16 h-16 sm:w-20 sm:h-20 xl:w-24 xl:h-24 bg-[#1A2D7E] flex items-center justify-center shadow-[0_6px_18px_-6px_rgba(26,45,126,0.45)] shrink-0">
              <span className="font-display font-black text-[#F08722] text-4xl sm:text-5xl xl:text-6xl leading-none">
                E
              </span>
              <span className="absolute -bottom-1 left-1/2 -translate-x-1/2 h-1 w-10 sm:w-12 bg-[#F08722]" />
            </div>
            <div className="font-display font-black uppercase tracking-tight leading-[0.92]">
              <div className="flex items-baseline gap-2 sm:gap-3 xl:gap-4">
                <span className="text-2xl sm:text-4xl xl:text-6xl text-[#1A2D7E]">EURO</span>
                <span className="text-2xl sm:text-4xl xl:text-6xl text-[#F08722]">CHAPE</span>
              </div>
              <svg
                viewBox="0 0 200 14"
                preserveAspectRatio="none"
                aria-hidden
                className="block mt-2 w-full h-3"
              >
                <path d="M 5 4 Q 100 12 195 4 Q 100 15 5 4 Z" fill="#F08722" />
              </svg>
            </div>
          </Link>
        </div>
      </div>

      {/* ROW 2 — NAV (slightly left of center) */}
      <div className="hidden xl:block border-t border-[#D5D5D0]/70 relative">
        <nav className="px-4 md:px-8 lg:px-14 flex items-center justify-center gap-2 h-12 xl:-translate-x-[190px]">
          {NAV.map((n) =>
            n.items ? (
              <div
                key={n.label}
                className="relative"
                onMouseEnter={() => handleEnter(n.label)}
                onMouseLeave={handleLeave}
              >
                <button
                  data-testid={`nav-${slug(n.label)}`}
                  className={`flex items-center gap-1 px-4 py-2 text-xs uppercase tracking-[0.16em] font-semibold transition-colors ${
                    openMenu === n.label
                      ? "text-[#F08722]"
                      : "text-[#1A2D7E] hover:text-[#F08722]"
                  }`}
                >
                  {n.label}
                  <ChevronDown
                    className={`w-3.5 h-3.5 transition-transform ${
                      openMenu === n.label ? "rotate-180" : ""
                    }`}
                  />
                </button>
              </div>
            ) : (
              <NavLink
                key={n.to}
                to={n.to}
                end={n.to === "/"}
                data-testid={`nav-${slug(n.label)}`}
                className={({ isActive }) =>
                  `link-underline px-4 py-2 text-xs uppercase tracking-[0.16em] font-semibold transition-colors ${
                    isActive ? "text-[#F08722]" : "text-[#1A2D7E] hover:text-[#F08722]"
                  }`
                }
              >
                {n.label}
              </NavLink>
            )
          )}
        </nav>

        {/* Mega menu */}
        <AnimatePresence>
          {openMenu && (
            <motion.div
              initial={{ opacity: 0, y: -8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              transition={{ duration: 0.25, ease: "easeOut" }}
              onMouseEnter={() => handleEnter(openMenu)}
              onMouseLeave={handleLeave}
              data-testid={`mega-${slug(openMenu)}`}
              className="absolute left-0 right-0 top-full bg-[#F4F4F0] border-y border-[#D5D5D0] shadow-[0_24px_40px_-16px_rgba(26,45,126,0.12)]"
            >
              <MegaPanel data={NAV.find((x) => x.label === openMenu)} />
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Mobile drawer */}
      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.35, ease: [0.86, 0, 0.07, 1] }}
            className="xl:hidden overflow-hidden bg-[#F4F4F0] border-t border-[#D5D5D0]"
            data-testid="mobile-menu"
          >
            <div className="px-6 py-6 flex flex-col">
              {NAV.map((n) => {
                if (n.items) {
                  const isOpen = mobileSub === n.label;
                  return (
                    <div key={n.label} className="border-b border-[#D5D5D0]">
                      <button
                        onClick={() => setMobileSub(isOpen ? null : n.label)}
                        className="w-full flex items-center justify-between py-4 font-display font-bold text-2xl uppercase tracking-tight text-[#1A2D7E]"
                      >
                        <span>{n.label}</span>
                        <ChevronDown
                          className={`w-5 h-5 transition-transform ${
                            isOpen ? "rotate-180" : ""
                          }`}
                        />
                      </button>
                      <AnimatePresence>
                        {isOpen && (
                          <motion.div
                            initial={{ height: 0, opacity: 0 }}
                            animate={{ height: "auto", opacity: 1 }}
                            exit={{ height: 0, opacity: 0 }}
                            className="overflow-hidden"
                          >
                            <div className="pb-4 pl-1 space-y-3">
                              {n.items.map((it) => (
                                <Link
                                  key={it.to}
                                  to={it.to}
                                  className="block text-sm text-[#1A2D7E]"
                                >
                                  {it.label}
                                </Link>
                              ))}
                            </div>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>
                  );
                }
                return (
                  <NavLink
                    key={n.to}
                    to={n.to}
                    end={n.to === "/"}
                    data-testid={`mobile-nav-${slug(n.label)}`}
                    className="font-display font-bold text-2xl uppercase tracking-tight text-[#1A2D7E] py-4 border-b border-[#D5D5D0]"
                  >
                    {n.label}
                  </NavLink>
                );
              })}
              <Link
                to="/devis"
                data-testid="mobile-devis-cta"
                className="mt-6 inline-flex items-center justify-between bg-[#F08722] text-white px-6 py-4 text-sm uppercase tracking-[0.16em] font-semibold"
              >
                Demander un devis <ArrowUpRight className="w-4 h-4" />
              </Link>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}

function MegaPanel({ data }) {
  if (!data) return null;
  return (
    <div className="px-6 md:px-12 lg:px-24 py-12 grid grid-cols-1 lg:grid-cols-12 gap-10">
      <div className="lg:col-span-4">
        <div className="text-xs uppercase tracking-[0.22em] text-[#F08722] font-semibold flex items-center gap-3">
          <span className="w-6 h-px bg-[#F08722]" />
          {data.label}
        </div>
        <h3 className="font-display font-bold uppercase tracking-tight text-3xl mt-5 leading-[1] text-[#1A2D7E]">
          {data.intro}
        </h3>
        <Link
          to="/services"
          className="mt-6 inline-flex items-center gap-2 text-xs uppercase tracking-[0.18em] font-semibold text-[#1A2D7E] hover:text-[#F08722] link-underline"
        >
          Voir tous les services <ArrowUpRight className="w-4 h-4" />
        </Link>
      </div>
      <div className="lg:col-span-8 grid grid-cols-1 md:grid-cols-2 gap-px bg-[#D5D5D0] border border-[#D5D5D0]">
        {data.items.map((it) => (
          <Link
            key={it.to}
            to={it.to}
            className="bg-[#F4F4F0] p-6 hover:bg-[#1A2D7E] hover:text-white transition-colors group"
          >
            <div className="flex items-center justify-between">
              <span className="font-display font-bold uppercase tracking-tight text-base">
                {it.label}
              </span>
              <ArrowUpRight className="w-4 h-4 text-[#F08722] group-hover:text-white" />
            </div>
            <p className="text-xs text-[#5C5E58] group-hover:text-[#E6E6E0] mt-2 leading-relaxed">
              {it.desc}
            </p>
          </Link>
        ))}
      </div>
    </div>
  );
}
